import React, { useEffect, useReducer, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  Row
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import {
  BiArrowBack,
  BiCheck,
  BiChevronRight,
  BiMenu,
  BiSolidBot,
  BiSolidCog,
  BiSolidDollarCircle,
  BiSolidHome,
  BiSolidLogOut,
  BiSolidPlug,
  BiSolidStar,
  BiSolidUser,
  BiSupport
} from "react-icons/bi";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  LanguageMapper,
  MultiLanguageUrl
} from "../../constants/multiLanguage.constant";
import { selectIsUserNew } from "../../features/auth/auth.slice";
import { selectKnowledgeCreate } from "../../features/knowledge.slice";
import usePermission from "../../hooks/usePermission";
import { HumanCloneFillIcon } from "../common/icons/humanClone.icon";
import { LogoIcon } from "../common/icons/logo.icon";
import avatarPlaceholder from "./avatar.png";
import PluseMenuComponent from "./pluse-menu.component";
import ProfileMenuComponent from "./profile-menu.component";
import { BusinessPartnerPlanIcon } from "./dashboard-menu.component";
import { useGetProfileQuery } from "../../app/services/auth-rtk.service";
import { SumUserTypeEnum } from "../../utils/SumUserTypeEnum";

const CustomToggle = React.forwardRef(
  (
    {
      children,
      onClick
    }: { children: React.ReactNode; onClick: (e: any) => void },
    ref: React.ForwardedRef<any>
  ) => (
    <span
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      onKeyDown={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      role="button"
      tabIndex={-1}
    >
      {children}
    </span>
  )
);

CustomToggle.displayName = "CustomToggle";

function DashboardHeaderComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const getQueryParam = (key: string): string | null => {
    const url = new URL(window.location.href);
    return url.searchParams.get(key);
  };
  const profile = useGetProfileQuery();
  const [isShowBusinessPartner, setIsShowBusinessPartner] = useState(false);

  useEffect(() => {
    if (profile?.data && profile?.data?.user_type == SumUserTypeEnum.BUSINESSPARTNER) {
      setIsShowBusinessPartner(true);
    }
  }, [profile.data]);

  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const is_user_new = useAppSelector(selectIsUserNew);
  const knowladgeCreate = useAppSelector(selectKnowledgeCreate);

  const authState = useAppSelector((state) => state.authState);

  const [showLogOutPopUp, setShowLogOutPopUp] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectHandler = (l: string) => {
    const url = new URL(window.location.href);
    const params = url.search || "";
    const from = pathname.replace(
      MultiLanguageUrl[i18n.language],
      MultiLanguageUrl[l]
    );

    i18n.changeLanguage(l);

    navigate(from + params, {
      replace: true
    });
  };

  const lngFlagMapper = {
    "en-US": "https://cdn.1cdn.app/application/UMD/24041507435389_US.png",
    "fr-FR": "https://cdn.1cdn.app/application/UMD/24041507435387_France.png"
  } as { [key: string]: string };

  // actions.ts
  const MAIN_MENU = "MAIN_MENU";
  const OPEN_LANGUAGE_DROPDOWN = "OPEN_LANGUAGE_DROPDOWN";
  const OPEN_PROFILE_DROPDOWN = "OPEN_PROFILE_DROPDOWN";

  const openMainMenu = () => ({
    type: MAIN_MENU
  });

  const openLanguageDropdown = () => ({
    type: OPEN_LANGUAGE_DROPDOWN
  });

  const openProfileDropdown = () => ({
    type: OPEN_PROFILE_DROPDOWN
  });

  interface MenuState {
    isMenuOpen: boolean;
    isLanguageDropdownOpen: boolean;
    isProfileDropdownOpen: boolean;
  }

  const initialState: MenuState = {
    isMenuOpen: true,
    isLanguageDropdownOpen: false,
    isProfileDropdownOpen: false
  };

  const reducer = (state: MenuState, action: any) => {
    switch (action.type) {
      case MAIN_MENU:
        return {
          isLanguageDropdownOpen: false,
          isProfileDropdownOpen: false,
          isMenuOpen: true
        };
      case OPEN_LANGUAGE_DROPDOWN:
        return {
          isLanguageDropdownOpen: true,
          isProfileDropdownOpen: false,
          isMenuOpen: false
        };
      case OPEN_PROFILE_DROPDOWN:
        return {
          isProfileDropdownOpen: true,
          isLanguageDropdownOpen: false,
          isMenuOpen: false
        };
      default:
        return state;
    }
  };

  const [state, internalDispatch] = useReducer(reducer, initialState);

  const renderProperContent = () => {
    if (state.isMenuOpen)
      return (
        <React.Fragment>
          {permissions.menu.home && (
            <DropdownItem
              as={Link}
              to=""
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname ==
                `/${MultiLanguageUrl[i18n.language] || "en-US"}/control-panel`
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              }`}
              onClick={() => setIsOpen(false)}
            >
              <BiSolidHome size={22} className="tw-mr-3" />
              {t("DashboardMenu/l-01")}
            </DropdownItem>
          )}

          {permissions.menu.widgets && (
            <DropdownItem
              as={Link}
              to="chatbots"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname ===
                `/${
                  MultiLanguageUrl[i18n.language] || "en-US"
                }/control-panel/chatbots`
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BiSolidBot size={22} className="tw-mr-3" />
              {t("DashboardMenu/l-02")}
            </DropdownItem>
          )}

          {permissions.menu.extension.self && (
            <DropdownItem
              as={Link}
              to="human-clones"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname ===
                `/${
                  MultiLanguageUrl[i18n.language] || "en-US"
                }/control-panel/human-clones`
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <HumanCloneFillIcon />
              <span className="tw-ml-3">{t("DashboardMenu/l-11")}</span>
            </DropdownItem>
          )}

          {/* TODO => Render base on proper permission */}
          <DropdownItem
            as={Link}
            to="rewards"
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
              location.pathname.includes("rewards")
                ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
            } tw-rounded-md`}
            onClick={() => setIsOpen(false)}
          >
            <BiSolidStar size={22} className="tw-mr-3" />
            {t("DashboardMenu/rewards")}
          </DropdownItem>

          <DropdownItem
            as={Link}
            target="_blank"
            to={`${import.meta.env.VITE_UI_ADDRESS}/${
              MultiLanguageUrl[i18n.language] || "en-US"
            }/chatbot-integration`}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
              location.pathname.includes("integrations")
                ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
            } tw-rounded-md`}
            onClick={() => setIsOpen(false)}
          >
            <BiSolidPlug size={22} className="tw-mr-3" />
            {t("DashboardBreadcrumb/Integrations")}
          </DropdownItem>

          {isShowBusinessPartner && (
            <DropdownItem
              as={Link}
              to="business-partner-panel"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("business-partner-panel")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BusinessPartnerPlanIcon className="tw-mr-3" />
              {t("DashboardMenu/l-13")}
            </DropdownItem>
          )}

          {permissions.menu.settings && (
            <DropdownItem
              as={Link}
              to="setting"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("setting")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BiSolidCog size={22} className="tw-mr-3" />
              {t("DashboardMenu/l-04")}
            </DropdownItem>
          )}

          {permissions.menu.profile && (
            <DropdownItem
              as={"button"}
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 tw-h-9 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("profile")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => internalDispatch(openProfileDropdown())}
            >
              <img
                src={authState?.profile?.user_avatar || avatarPlaceholder}
                alt="Profile"
                className="tw-w-6 tw-h-6 tw-mr-3 tw-rounded-full"
              />
              {authState?.profile?.user_first_name || "Profile"}
              <BiChevronRight size={20} className="tw-ml-auto" />
            </DropdownItem>
          )}

          <DropdownItem
            as={"button"}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 tw-h-9 tw-px-4 tw-py-2.5 tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800 tw-rounded-md`}
            onClick={() => internalDispatch(openLanguageDropdown())}
          >
            <img
              src={lngFlagMapper[MultiLanguageUrl[i18n.language]]}
              alt="Language"
              className="tw-w-6 tw-h-6 tw-mr-3 tw-rounded-full"
            />
            {LanguageMapper[i18n.language || "en"]}
            <BiChevronRight size={22} className="tw-ml-auto" />
          </DropdownItem>
        </React.Fragment>
      );
    if (state.isLanguageDropdownOpen)
      return (
        <React.Fragment>
          <DropdownItem
            as={"button"}
            onClick={() => internalDispatch(openMainMenu())}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 tw-bg-gray-800 tw-px-4 tw-py-2`}
          >
            <BiArrowBack size={22} className="tw-mr-3" />
            Back to Main Menu
          </DropdownItem>

          <DropdownItem
            as={"button"}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-h-9 tw-px-4 tw-py-2.5 tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800 tw-rounded-md`}
            onClick={() => selectHandler("en")}
          >
            <img
              src={lngFlagMapper["en-US"]}
              alt="Language"
              className="tw-w-6 tw-h-6 tw-mr-3 tw-rounded-full"
            />
            {LanguageMapper["en"]}
            {i18n.language === "en" && (
              <BiCheck size={22} className="tw-ml-auto tw-text-blue-500" />
            )}
          </DropdownItem>

          <DropdownItem
            as={"button"}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-h-9 tw-px-4 tw-py-2.5 tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800 tw-rounded-md`}
            onClick={() => selectHandler("fr")}
          >
            <img
              src={lngFlagMapper["fr-FR"]}
              alt="Language"
              className="tw-w-6 tw-h-6 tw-mr-3 tw-rounded-full"
            />
            {LanguageMapper["fr"]}
            {i18n.language === "fr" && (
              <BiCheck size={22} className="tw-ml-auto tw-text-blue-500" />
            )}
          </DropdownItem>
        </React.Fragment>
      );
    if (state.isProfileDropdownOpen)
      return (
        <React.Fragment>
          <DropdownItem
            as={"button"}
            onClick={() => internalDispatch(openMainMenu())}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 tw-bg-gray-800 tw-px-4 tw-py-2`}
          >
            <BiArrowBack size={22} className="tw-mr-3" />
            Back to Main Menu
          </DropdownItem>

          {permissions.menu.billing && (
            <DropdownItem
              as={Link}
              to="billing"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("billing")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BiSolidDollarCircle size={22} className="tw-mr-3" />
              {t("DashboardMenu/l-03")}
            </DropdownItem>
          )}

          {permissions.menu.profile && (
            <DropdownItem
              as={Link}
              to="profile"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("profile")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BiSolidUser size={26} className="tw-mr-3" />
              {t("DashboardMenu/l-07")}
            </DropdownItem>
          )}

          {permissions.menu.support && (
            <DropdownItem
              as={Link}
              to="contact"
              className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-px-4 tw-py-2.5 ${
                location.pathname.includes("contact")
                  ? "tw-bg-gray-900 active:tw-bg-gray-900 hover:tw-bg-gray-900"
                  : "tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800"
              } tw-rounded-md`}
              onClick={() => setIsOpen(false)}
            >
              <BiSupport size={22} className="tw-mr-3" />
              {t("DashboardMenu/l-05")}
            </DropdownItem>
          )}

          <DropdownItem
            as={"button"}
            onClick={() => setShowLogOutPopUp(true)}
            className={`tw-justify-start tw-items-center tw-inline-flex tw-text-gray-100 focus:tw-text-gray-100 hover:tw-text-gray-100 active:tw-text-gray-100 tw-bg-gray-800 active:tw-bg-gray-800 hover:tw-bg-gray-800 tw-px-4 tw-py-2.5`}
          >
            <BiSolidLogOut size={22} className="tw-mr-3" />
            {t("ProfilePage/btn-03")}
          </DropdownItem>
        </React.Fragment>
      );
  };

  const renderBreadcrumbs = (urlPath: string) => {
    // Check if the path starts with /control-panel/
    if (
      !urlPath.startsWith(`/${MultiLanguageUrl[i18n.language]}/control-panel/`)
    ) {
      return null; // No breadcrumbs for paths outside /control-panel/
    }

    if (
      urlPath.includes(
        `/${MultiLanguageUrl[i18n.language]}/control-panel/billing-create`
      )
    ) {
      return t("BillingPage/btn-03");
    }

    // Remove the starting /control-panel/ part
    const pathSegments = urlPath
      .slice(`/${MultiLanguageUrl[i18n.language]}/control-panel/`.length)
      .split("/");

    // Capitalize first letter of each segment
    const capitalizedSegments = pathSegments.map(
      (segment) => segment.charAt(0).toUpperCase() + segment.slice(1)
    );

    // Handle the last segment: remove hyphens and capitalize words
    const lastSegment = capitalizedSegments[capitalizedSegments.length - 1];
    const lastSegmentWords = lastSegment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    capitalizedSegments[capitalizedSegments.length - 1] =
      lastSegmentWords.join(" ");

    const textMapper: Record<string, string> = {
      Billing: t("DashboardBreadcrumb/Billing"),
      Profile: t("DashboardBreadcrumb/Profile"),
      Contact: t("DashboardBreadcrumb/Contact"),
      Setting: t("DashboardBreadcrumb/Setting"),
      Integrations: t("DashboardBreadcrumb/Integrations"),
      Rewards: t("DashboardBreadcrumb/Rewards"),
      IntegrationsBlog: t("DashboardBreadcrumb/IntegrationsBlog"),
      "Human Clones": t("DashboardBreadcrumb/HumanClone"),
      Editor:
        t("DashboardBreadcrumb/Editor") +
        ` ${
          knowladgeCreate.chatbotTitle
            ? "> " + knowladgeCreate.chatbotTitle
            : ""
        }`
    };

    // Join segments with a space as separator
    const breadcrumbString = capitalizedSegments
      .map((item) => textMapper[item] ?? item)
      .join(" > ");

    if (urlPath.includes("chatbot-edit")) {
      return getQueryParam("title");
    }

    return breadcrumbString;
  };

  useEffect(() => {
    if (!isOpen) {
      internalDispatch(openMainMenu());
    }
  }, [isOpen]);

  return (
    <React.Fragment>
      <div className="tw-w-full tw-h-16 tw-bg-white tw-justify-start tw-items-center tw-inline-flex tw-gap-3 tw-border-solid tw-border-0 tw-border-b tw-border-gray-100">
        <Link
          to={""}
          className="tw-w-16 tw-self-stretch tw-bg-primary tw-justify-center tw-items-center md:tw-hidden tw-flex"
        >
          <LogoIcon theme={"white"} width={"40px"} height={"40px"} />
        </Link>
        <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-center tw-items-start tw-inline-flex">
          <div className="md:tw-px-4 tw-self-stretch tw-justify-start tw-items-center tw-gap-4 tw-inline-flex">
            <div
              title={renderBreadcrumbs(window.location.pathname) || "LouiseBot"}
              className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 md:tw-text-lg tw-text-sm  tw-font-semibold tw-font-['Lexend'] tw-leading-none tw-truncate"
            >
              <span className="lg:tw-hidden tw-block">
                {(renderBreadcrumbs(window.location.pathname) || "LouiseBot")
                  .length > 35
                  ? (
                      renderBreadcrumbs(window.location.pathname) || "LouiseBot"
                    ).substring(0, 35) + " ..."
                  : renderBreadcrumbs(window.location.pathname) || "LouiseBot"}
              </span>
              <span className="lg:tw-block tw-hidden">
                {renderBreadcrumbs(window.location.pathname) || "LouiseBot"}
              </span>
            </div>
          </div>
        </div>
        <div className="tw-min-w-10 tw-justify-center tw-items-center tw-gap-10 tw-flex">
          <div className="tw-justify-end tw-items-center tw-flex">
            {/* TODO => Place of notifications area */}
            {/* <div className="tw-p-1 tw-bg-white tw-rounded-2xl tw-justify-center tw-items-center tw-flex">
              <div className="tw-w-6 tw-h-6 tw-relative" />
            </div> */}

            {/* Profile image area */}
            <ProfileMenuComponent />

            {/* Add new button area */}
            {!is_user_new && <PluseMenuComponent />}

            {/* Side menu toggler */}
            <Dropdown
              autoClose={"outside"}
              onToggle={toggleDropdown}
              show={isOpen}
            >
              <DropdownToggle as={CustomToggle}>
                <button className="tw-p-1 tw-bg-white tw-rounded-2xl tw-justify-center tw-items-center md:tw-hidden tw-flex tw-border-0">
                  <BiMenu size={20} />
                </button>
              </DropdownToggle>
              <DropdownMenu className="tw-min-w-[270px] tw-bg-gray-800 tw-p-2">
                {renderProperContent()}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Logout modal */}
      <Modal
        show={showLogOutPopUp}
        centered
        onHide={() => setShowLogOutPopUp(false)}
      >
        <Row className="m-0">
          <Col className="tw-pt-4 tw-ps-4">
            <AiOutlineClose
              className="tw-cursor-pointer"
              onClick={() => setShowLogOutPopUp(false)}
            />
          </Col>
        </Row>
        <Modal.Body className="text-center tw-px-14 tw-pb-14 tw-pt-20 ">
          <Row>
            <Col className="tw-flex  tw-justify-center">
              <div className="tw-border-solid tw-w-20 tw-h-20 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border-slate-50 tw-border-[12px]">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_2596_3315)">
                    <path
                      d="M17.964 3.13288C17.766 2.78801 17.4806 2.50148 17.1364 2.30223C16.7923 2.10297 16.4017 1.99805 16.004 1.99805C15.6064 1.99805 15.2157 2.10297 14.8716 2.30223C14.5275 2.50148 14.242 2.78801 14.044 3.13288L0.330014 26.4669C-0.583986 28.0229 0.512014 30.0009 2.29001 30.0009H29.716C31.494 30.0009 32.592 28.0209 31.676 26.4669L17.964 3.13288ZM16 10.0009C17.07 10.0009 17.908 10.9249 17.8 11.9909L17.1 19.0049C17.0765 19.2804 16.9504 19.5371 16.7467 19.7241C16.543 19.9112 16.2766 20.015 16 20.015C15.7235 20.015 15.457 19.9112 15.2533 19.7241C15.0496 19.5371 14.9235 19.2804 14.9 19.0049L14.2 11.9909C14.1749 11.7393 14.2027 11.4853 14.2817 11.2452C14.3607 11.0051 14.4891 10.7842 14.6587 10.5967C14.8283 10.4092 15.0352 10.2594 15.2663 10.1567C15.4973 10.0541 15.7472 10.001 16 10.0009ZM16.004 22.0009C16.5344 22.0009 17.0432 22.2116 17.4182 22.5867C17.7933 22.9617 18.004 23.4704 18.004 24.0009C18.004 24.5313 17.7933 25.04 17.4182 25.4151C17.0432 25.7902 16.5344 26.0009 16.004 26.0009C15.4736 26.0009 14.9649 25.7902 14.5898 25.4151C14.2147 25.04 14.004 24.5313 14.004 24.0009C14.004 23.4704 14.2147 22.9617 14.5898 22.5867C14.9649 22.2116 15.4736 22.0009 16.004 22.0009Z"
                      fill="#FDB139"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2596_3315">
                      <rect width="32" height="32" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </Col>
          </Row>
          <Row className="tw-mt-8">
            <Col>
              <h2 className="font-sofiaSans tw-text-sm ">
                {t("DashboardMenu/l-08")}
              </h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <p className="font-sofiaSans  tw-text-zinc-500 ">
                {t("DashboardMenu/text-01")}
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <Link
                to={`/${MultiLanguageUrl[i18n.language] || "en-US"}/logout`}
                className="btn btn-danger tw-bg-rose-50 tw-flex tw-justify-center"
              >
                {t("DashboardMenu/l-08")}
              </Link>
            </Col>
          </Row>
          <Row className="tw-mt-5">
            <Col>
              <Button
                variant="primary"
                className="w-100 tw-flex tw-justify-center"
                onClick={() => setShowLogOutPopUp(false)}
              >
                {t("DashboardMenu/l-09")}
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
}

export default DashboardHeaderComponent;
